/**
 * Food Safety dashboard constants — keep labels aligned with express_app.
 */

export const FOOD_SAFETY_SECTION = Object.freeze({
  FRIDGES_FREEZERS: 'fridges_freezers',
  COOKING_REHEATING: 'cooking_reheating',
  COOLING: 'cooling',
  DELIVERIES: 'deliveries',
  CLEANING_CHECKLISTS: 'cleaning_checklists',
  HOT_HOLDING: 'hot_holding',
  COLD_HOLDING: 'cold_holding',
  PROBE_CALIBRATION: 'probe_calibration',
  DISHWASHERS: 'dishwashers',
  LEGACY_EQUIPMENT: 'legacy_equipment',
  REPORTS: 'reports',
})

/** Sections every dashboard build understands (mirrors Express). */
export const FOOD_SAFETY_V1_SECTIONS = Object.freeze([
  FOOD_SAFETY_SECTION.FRIDGES_FREEZERS,
  FOOD_SAFETY_SECTION.COOKING_REHEATING,
  FOOD_SAFETY_SECTION.COOLING,
  FOOD_SAFETY_SECTION.DELIVERIES,
  FOOD_SAFETY_SECTION.CLEANING_CHECKLISTS,
])

/** Only returned by Express when the capability header says `v2`. */
export const FOOD_SAFETY_V2_SECTIONS = Object.freeze([
  FOOD_SAFETY_SECTION.HOT_HOLDING,
  FOOD_SAFETY_SECTION.COLD_HOLDING,
  FOOD_SAFETY_SECTION.PROBE_CALIBRATION,
])

export const FOOD_SAFETY_ALL_SECTIONS = Object.freeze([
  ...FOOD_SAFETY_V1_SECTIONS,
  ...FOOD_SAFETY_V2_SECTIONS,
  FOOD_SAFETY_SECTION.DISHWASHERS,
])

/** Event-driven holding sections, logged against the venue's holding range. */
export const FOOD_SAFETY_HOLDING_SECTIONS = Object.freeze([
  FOOD_SAFETY_SECTION.HOT_HOLDING,
  FOOD_SAFETY_SECTION.COLD_HOLDING,
])

/**
 * Express only sends V2 sections, statuses and exceptions to clients that
 * declare them; every Food Safety request from this build sends this header.
 */
export const FOOD_SAFETY_CAPABILITIES_HEADER = 'X-OMS-Food-Safety-Capabilities'
export const FOOD_SAFETY_CAPABILITY_V2 = 'v2'
export const FOOD_SAFETY_CLIENT_CAPABILITIES = `${FOOD_SAFETY_CAPABILITY_V2},equipment_sections`

export const FOOD_SAFETY_SECTION_LABELS = Object.freeze({
  [FOOD_SAFETY_SECTION.FRIDGES_FREEZERS]: 'Fridges & Freezers',
  [FOOD_SAFETY_SECTION.COOKING_REHEATING]: 'Cooking & Reheating',
  [FOOD_SAFETY_SECTION.COOLING]: 'Cooling',
  [FOOD_SAFETY_SECTION.DELIVERIES]: 'Deliveries',
  [FOOD_SAFETY_SECTION.CLEANING_CHECKLISTS]: 'Cleaning & Checklists',
  [FOOD_SAFETY_SECTION.HOT_HOLDING]: 'Hot Holding',
  [FOOD_SAFETY_SECTION.COLD_HOLDING]: 'Cold Holding',
  [FOOD_SAFETY_SECTION.PROBE_CALIBRATION]: 'Temperature Probes',
  [FOOD_SAFETY_SECTION.DISHWASHERS]: 'Dishwashers',
  [FOOD_SAFETY_SECTION.LEGACY_EQUIPMENT]: 'Unclassified historical equipment',
  [FOOD_SAFETY_SECTION.REPORTS]: 'Reports',
})

/**
 * How a section is logged on Home: on a schedule, or when it happens.
 * Express sends `logMode` on each home section.
 */
export const FOOD_SAFETY_SECTION_LOG_MODE = Object.freeze({
  SCHEDULED: 'scheduled',
  EVENT_DRIVEN: 'event_driven',
})

export const FOOD_SAFETY_SECTION_LOG_MODE_IDLE_COPY = Object.freeze({
  [FOOD_SAFETY_SECTION_LOG_MODE.SCHEDULED]: 'Nothing scheduled',
  [FOOD_SAFETY_SECTION_LOG_MODE.EVENT_DRIVEN]: 'Log when needed',
})

/** Small tag on Home tiles: checks on a schedule vs records when it happens. */
export const FOOD_SAFETY_SECTION_LOG_MODE_TAGS = Object.freeze({
  [FOOD_SAFETY_SECTION_LOG_MODE.SCHEDULED]: 'Scheduled',
  [FOOD_SAFETY_SECTION_LOG_MODE.EVENT_DRIVEN]: 'As it happens',
})

export const FOOD_SAFETY_HOME_STATUS = Object.freeze({
  ALL_DONE: 'all_done',
  ATTENTION: 'attention',
  READY: 'ready',
  IN_PROGRESS: 'in_progress',
  IDLE: 'idle',
})

export const FOOD_SAFETY_HOME_STATUS_LABELS = Object.freeze({
  [FOOD_SAFETY_HOME_STATUS.ALL_DONE]: 'All done',
  [FOOD_SAFETY_HOME_STATUS.ATTENTION]: 'Needs attention',
  [FOOD_SAFETY_HOME_STATUS.READY]: 'Ready to check',
  [FOOD_SAFETY_HOME_STATUS.IN_PROGRESS]: 'In progress',
  [FOOD_SAFETY_HOME_STATUS.IDLE]: 'Nothing scheduled',
})

/** Secondary copy on white cards — darker than theme oliveGrey for WCAG-ish contrast. */
export const FOOD_SAFETY_SECONDARY_TEXT = '#555555'

/**
 * Shared Food Safety surface tokens (home + light section consistency).
 * OMS lime/olive/mayo — no purple gradients, neon glow, or game chrome.
 */
export const FOOD_SAFETY_SURFACE = Object.freeze({
  CARD_RADIUS: '8px',
  CONTROL_RADIUS: '6px',
  /** Soft lime wash behind the home hero (title + actions + day status). */
  HERO_BG: 'linear-gradient(180deg, #f7fbec 0%, #ffffff 85%)',
  HERO_BORDER: '#e4efc8',
  LIME_WASH: '#f3f8e8',
  LIME_EDGE: '#d4e8a0',
  CARD_SHADOW: '0 1px 2px rgba(19, 45, 7, 0.04)',
  CARD_SHADOW_HOVER: '0 2px 10px rgba(19, 45, 7, 0.07)',
  STATUS_DONE: '#5a9e0b',
  STATUS_ATTENTION: '#c45c26',
  STATUS_READY: '#132d07',
})

/** Home hero supporting line under the Food Safety title. */
export const FOOD_SAFETY_HOME_INTRO =
  'Today’s kitchen checks — temps, cooks, cools, deliveries, and cleaning.'

/** Home line when the venue has no fridge or freezer yet. */
export const FOOD_SAFETY_HOME_NO_FRIDGES_LINE =
  'Name your fridges to start temperature checks. Cooking, cooling, hot holding and deliveries can be logged straight away by typing the food.'

/** One-line Setup hint: shortcuts are optional saved names. */
export const FOOD_SAFETY_QUICK_PICK_HINT =
  'Optional. Save shortcuts for names you use often — tap them on the day to log faster.'

/**
 * Max quick-pick chips shown before “Show more” (needed picks sort first).
 * Keeps Cooking / Cooling / Deliveries usable when a venue has dozens of templates.
 */
export const FOOD_SAFETY_QUICK_PICK_CHIP_MAX = 8

export const FOOD_SAFETY_HISTORY_LOCKED_CODE = 'FOOD_SAFETY_HISTORY_LOCKED'

export const FOOD_SAFETY_RESPONSE_TYPE = Object.freeze({
  NUMBER: 'number',
  COMPLETE: 'complete',
  DELIVERY: 'delivery',
  COOLING: 'cooling',
  CALIBRATION: 'calibration',
})

/** 403 code when the venue role cannot complete / manage Food Safety. */
export const FOOD_SAFETY_PERMISSION_DENIED_CODE =
  'FOOD_SAFETY_PERMISSION_DENIED'

/** Ad-hoc sections that use the fast log pattern (Step 13). */
export const FOOD_SAFETY_ADHOC_SECTIONS = Object.freeze([
  FOOD_SAFETY_SECTION.COOKING_REHEATING,
  FOOD_SAFETY_SECTION.COOLING,
  FOOD_SAFETY_SECTION.DELIVERIES,
  FOOD_SAFETY_SECTION.HOT_HOLDING,
  FOOD_SAFETY_SECTION.COLD_HOLDING,
])

export const FOOD_SAFETY_ADHOC_SECTION_META = Object.freeze({
  [FOOD_SAFETY_SECTION.COOKING_REHEATING]: {
    responseType: FOOD_SAFETY_RESPONSE_TYPE.NUMBER,
    namePlaceholder: 'e.g. Chicken curry',
    tempLabel: 'Core temp',
    emptyHint: 'Log a cook or reheat when it happens.',
  },
  [FOOD_SAFETY_SECTION.COOLING]: {
    responseType: FOOD_SAFETY_RESPONSE_TYPE.COOLING,
    namePlaceholder: 'e.g. Bolognese batch',
    tempLabel: 'Final temp',
    tempHelper: 'When cool — not cook core temp.',
    emptyHint: 'Log the final cooled temp when the batch is ready.',
  },
  [FOOD_SAFETY_SECTION.DELIVERIES]: {
    responseType: FOOD_SAFETY_RESPONSE_TYPE.DELIVERY,
    namePlaceholder: 'e.g. Chicken thighs',
    tempLabel: 'Delivery temp',
    emptyHint: 'Log a delivery when it arrives.',
  },
  [FOOD_SAFETY_SECTION.HOT_HOLDING]: {
    responseType: FOOD_SAFETY_RESPONSE_TYPE.NUMBER,
    namePlaceholder: 'e.g. Bain-marie — chilli',
    tempLabel: 'Holding temp',
    emptyHint: 'Check food kept hot for service — log it as you go.',
  },
  [FOOD_SAFETY_SECTION.COLD_HOLDING]: {
    responseType: FOOD_SAFETY_RESPONSE_TYPE.NUMBER,
    namePlaceholder: 'e.g. Salad bar — coleslaw',
    tempLabel: 'Holding temp',
    emptyHint: 'Check food kept cold on display — log it as you go.',
  },
})

export const FOOD_SAFETY_FREQUENCY = Object.freeze({
  DAILY: 'daily',
  WEEKLY: 'weekly',
  BIWEEKLY: 'biweekly',
  MONTHLY: 'monthly',
})

export const FOOD_SAFETY_FREQUENCY_LABELS = Object.freeze({
  [FOOD_SAFETY_FREQUENCY.DAILY]: 'Daily',
  [FOOD_SAFETY_FREQUENCY.WEEKLY]: 'Weekly',
  [FOOD_SAFETY_FREQUENCY.BIWEEKLY]: 'Every 2 weeks',
  [FOOD_SAFETY_FREQUENCY.MONTHLY]: 'Monthly',
})

export const FOOD_SAFETY_SCHEDULE_MODE = Object.freeze({
  SCHEDULED: 'scheduled',
  AD_HOC: 'ad_hoc',
})

export const FOOD_SAFETY_SETUP_SECTIONS = Object.freeze([
  FOOD_SAFETY_SECTION.FRIDGES_FREEZERS,
  FOOD_SAFETY_SECTION.COOKING_REHEATING,
  FOOD_SAFETY_SECTION.COOLING,
  FOOD_SAFETY_SECTION.HOT_HOLDING,
  FOOD_SAFETY_SECTION.COLD_HOLDING,
  FOOD_SAFETY_SECTION.DELIVERIES,
  FOOD_SAFETY_SECTION.PROBE_CALIBRATION,
  FOOD_SAFETY_SECTION.DISHWASHERS,
  FOOD_SAFETY_SECTION.CLEANING_CHECKLISTS,
])

/** Mirrors express FOOD_SAFETY_SECTION_DEFAULTS for create forms. */
export const FOOD_SAFETY_SECTION_DEFAULTS = Object.freeze({
  [FOOD_SAFETY_SECTION.FRIDGES_FREEZERS]: {
    scheduleMode: FOOD_SAFETY_SCHEDULE_MODE.SCHEDULED,
    responseType: FOOD_SAFETY_RESPONSE_TYPE.NUMBER,
    frequency: FOOD_SAFETY_FREQUENCY.DAILY,
    allowsMultiplePerDay: true,
  },
  [FOOD_SAFETY_SECTION.COOKING_REHEATING]: {
    scheduleMode: FOOD_SAFETY_SCHEDULE_MODE.AD_HOC,
    responseType: FOOD_SAFETY_RESPONSE_TYPE.NUMBER,
    frequency: null,
    allowsMultiplePerDay: true,
  },
  [FOOD_SAFETY_SECTION.COOLING]: {
    scheduleMode: FOOD_SAFETY_SCHEDULE_MODE.AD_HOC,
    responseType: FOOD_SAFETY_RESPONSE_TYPE.COOLING,
    frequency: null,
    allowsMultiplePerDay: true,
  },
  [FOOD_SAFETY_SECTION.DELIVERIES]: {
    scheduleMode: FOOD_SAFETY_SCHEDULE_MODE.AD_HOC,
    responseType: FOOD_SAFETY_RESPONSE_TYPE.DELIVERY,
    frequency: null,
    allowsMultiplePerDay: true,
  },
  [FOOD_SAFETY_SECTION.CLEANING_CHECKLISTS]: {
    scheduleMode: FOOD_SAFETY_SCHEDULE_MODE.SCHEDULED,
    responseType: FOOD_SAFETY_RESPONSE_TYPE.COMPLETE,
    frequency: FOOD_SAFETY_FREQUENCY.DAILY,
    allowsMultiplePerDay: false,
  },
  [FOOD_SAFETY_SECTION.HOT_HOLDING]: {
    scheduleMode: FOOD_SAFETY_SCHEDULE_MODE.AD_HOC,
    responseType: FOOD_SAFETY_RESPONSE_TYPE.NUMBER,
    frequency: null,
    allowsMultiplePerDay: true,
  },
  [FOOD_SAFETY_SECTION.COLD_HOLDING]: {
    scheduleMode: FOOD_SAFETY_SCHEDULE_MODE.AD_HOC,
    responseType: FOOD_SAFETY_RESPONSE_TYPE.NUMBER,
    frequency: null,
    allowsMultiplePerDay: true,
  },
  /** Ad hoc unless Setup turns on "Check on a schedule". */
  [FOOD_SAFETY_SECTION.DISHWASHERS]: {
    scheduleMode: FOOD_SAFETY_SCHEDULE_MODE.AD_HOC,
    responseType: FOOD_SAFETY_RESPONSE_TYPE.CALIBRATION,
    frequency: null,
    allowsMultiplePerDay: true,
  },
  [FOOD_SAFETY_SECTION.PROBE_CALIBRATION]: {
    scheduleMode: FOOD_SAFETY_SCHEDULE_MODE.AD_HOC,
    responseType: FOOD_SAFETY_RESPONSE_TYPE.CALIBRATION,
    frequency: null,
    allowsMultiplePerDay: true,
  },
})

/** Cleaning: a job is one sign-off; a checklist is steps. Missing means checklist. */
export const FOOD_SAFETY_TASK_KIND = Object.freeze({
  JOB: 'job',
  CHECKLIST: 'checklist',
})

/** Optional extra fridge/freezer temps the same day (due is per slot). */
export const FOOD_SAFETY_FRIDGE_MAX_LOGS_PER_DAY = 5
export const FOOD_SAFETY_FRIDGE_MAX_DUE_TIMES =
  FOOD_SAFETY_FRIDGE_MAX_LOGS_PER_DAY

/**
 * Temp thresholds when free-typing (or a template has no min/max).
 * Mirrors express FOOD_SAFETY_ADHOC_TEMP_DEFAULTS — cook ≥75°C, cool ≤8°C,
 * delivery ≤5°C (chilled; arriving colder is never flagged).
 *
 * Delivery ambient templates set config.tempBand = 'ambient' and skip defaults.
 */
export const FOOD_SAFETY_ADHOC_TEMP_DEFAULTS = Object.freeze({
  [FOOD_SAFETY_SECTION.COOKING_REHEATING]: Object.freeze({ tempMin: 75 }),
  [FOOD_SAFETY_SECTION.COOLING]: Object.freeze({ tempMax: 8 }),
  [FOOD_SAFETY_SECTION.DELIVERIES]: Object.freeze({ tempMax: 5 }),
  [FOOD_SAFETY_SECTION.HOT_HOLDING]: Object.freeze({ tempMin: 63 }),
  [FOOD_SAFETY_SECTION.COLD_HOLDING]: Object.freeze({ tempMax: 8 }),
})

/** Delivery Setup presets — chilled is the free-type / blank default. */
export const FOOD_SAFETY_DELIVERY_TEMP_BAND = Object.freeze({
  CHILLED: 'chilled',
  FROZEN: 'frozen',
  AMBIENT: 'ambient',
})

/**
 * Same as Express: deliveries are only checked against an upper limit, so
 * colder is never a failure. Past logs keep the range they were saved with.
 */
export const FOOD_SAFETY_DELIVERY_TEMP_PRESETS = Object.freeze({
  [FOOD_SAFETY_DELIVERY_TEMP_BAND.CHILLED]: Object.freeze({
    label: 'Chilled',
    tempMin: null,
    tempMax: 5,
  }),
  [FOOD_SAFETY_DELIVERY_TEMP_BAND.FROZEN]: Object.freeze({
    label: 'Frozen',
    tempMin: null,
    tempMax: -12,
  }),
  [FOOD_SAFETY_DELIVERY_TEMP_BAND.AMBIENT]: Object.freeze({
    label: 'Ambient',
    tempMin: null,
    tempMax: null,
  }),
})

/** Why a delivery went back. A short note stays separate. */
export const FOOD_SAFETY_DELIVERY_REJECTION_CODE = Object.freeze({
  DAMAGED_PACKAGING: 'damaged_packaging',
  SPILLED_OR_WET_PACKAGING: 'spilled_or_wet_packaging',
  WRONG_ITEMS: 'wrong_items',
})

export const FOOD_SAFETY_DELIVERY_REJECTION_LABELS = Object.freeze({
  [FOOD_SAFETY_DELIVERY_REJECTION_CODE.DAMAGED_PACKAGING]: 'Damaged packaging',
  [FOOD_SAFETY_DELIVERY_REJECTION_CODE.SPILLED_OR_WET_PACKAGING]:
    'Spilled or wet packaging',
  [FOOD_SAFETY_DELIVERY_REJECTION_CODE.WRONG_ITEMS]: 'Wrong items',
})

export const FOOD_SAFETY_DELIVERY_REJECTION_OPTIONS = Object.freeze(
  Object.entries(FOOD_SAFETY_DELIVERY_REJECTION_LABELS).map(
    ([code, label]) => ({
      code,
      label,
    }),
  ),
)

/** This named product, or every temperature on the drop. */
export const FOOD_SAFETY_DELIVERY_REJECTION_SCOPE = Object.freeze({
  PRODUCT: 'product',
  DROP: 'drop',
})

export const FOOD_SAFETY_DELIVERY_REJECTION_SCOPE_LABELS = Object.freeze({
  [FOOD_SAFETY_DELIVERY_REJECTION_SCOPE.PRODUCT]: 'This product only',
  [FOOD_SAFETY_DELIVERY_REJECTION_SCOPE.DROP]: 'The whole drop',
})

/** Minimums older templates were saved with (0–5°C, −18 to −12°C). */
export const FOOD_SAFETY_DELIVERY_LEGACY_MINS = Object.freeze({
  [FOOD_SAFETY_DELIVERY_TEMP_BAND.CHILLED]: 0,
  [FOOD_SAFETY_DELIVERY_TEMP_BAND.FROZEN]: -18,
})

/**
 * Words in a product name that suggest its band, for the receipt picker's
 * first guess (staff can change it). Checked frozen first, then ambient;
 * anything else starts as Chilled.
 */
export const FOOD_SAFETY_DELIVERY_BAND_KEYWORDS = Object.freeze({
  [FOOD_SAFETY_DELIVERY_TEMP_BAND.FROZEN]: Object.freeze([
    'frozen',
    'ice cream',
    'icecream',
    'sorbet',
    'gelato',
    'iqf',
    'frz',
  ]),
  [FOOD_SAFETY_DELIVERY_TEMP_BAND.AMBIENT]: Object.freeze([
    'tinned',
    'canned',
    'tin',
    'can',
    'jar',
    'dried',
    'flour',
    'sugar',
    'salt',
    'rice',
    'oil',
    'vinegar',
    'ambient',
    'bottled',
    'water',
    'crisps',
    'napkins',
    'foil',
    'cling film',
    'bags',
    'gloves',
    'cleaner',
    'detergent',
  ]),
})

export const FOOD_SAFETY_WEEKDAY_OPTIONS = Object.freeze([
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
  { value: 0, label: 'Sun' },
])

export const FOOD_SAFETY_WEEK_PRESET = Object.freeze({
  THIS_WEEK: 'this_week',
  LAST_WEEK: 'last_week',
  OLDER: 'older',
})

export const FOOD_SAFETY_WEEK_PRESET_LABELS = Object.freeze({
  [FOOD_SAFETY_WEEK_PRESET.THIS_WEEK]: 'This Week',
  [FOOD_SAFETY_WEEK_PRESET.LAST_WEEK]: 'Last Week',
  [FOOD_SAFETY_WEEK_PRESET.OLDER]: 'Older Reports',
})

/**
 * Cell statuses from Express week-grid. A unit's check is out of service (not
 * missed) only when the unit was out at its due-by time.
 */
export const FOOD_SAFETY_REPORT_CELL_STATUS = Object.freeze({
  COMPLETED: 'completed',
  PARTIAL: 'partial',
  MISSED: 'missed',
  NOT_DUE: 'not_due',
  OUT_OF_SERVICE: 'out_of_service',
  /** Kitchen closed that day. Not missed, and not an empty cell. */
  CLOSED: 'closed',
  LOGGED: 'logged',
  NONE: 'none',
  LOCKED: 'locked',
})

/** One check's outcome in report day detail (same rules as its grid cell). */
export const FOOD_SAFETY_REPORT_ITEM_STATUS = Object.freeze({
  COMPLETED: 'completed',
  MISSED: 'missed',
  NOT_DUE: 'not_due',
  OUT_OF_SERVICE: 'out_of_service',
  /** Kitchen shut. Not missed, and not left blank. */
  CLOSED: 'closed',
  LOGGED: 'logged',
  NOT_SCHEDULED: 'not_scheduled',
})

/** Exception overlay on a report cell / day, alongside the cell status. */
export const FOOD_SAFETY_REPORT_EXCEPTION_STATE = Object.freeze({
  NONE: 'none',
  ACTIONED: 'actioned',
  UNRESOLVED: 'unresolved',
})

export const FOOD_SAFETY_REPORT_EXCEPTION_STATE_LABELS = Object.freeze({
  [FOOD_SAFETY_REPORT_EXCEPTION_STATE.ACTIONED]:
    'Exception · corrective action recorded',
  [FOOD_SAFETY_REPORT_EXCEPTION_STATE.UNRESOLVED]: 'Exception · unresolved',
})

/**
 * Why an exception was opened. One shared model for every section; the
 * original log is never changed.
 */
export const FOOD_SAFETY_EXCEPTION_KIND = Object.freeze({
  OUT_OF_RANGE: 'out_of_range',
  DELIVERY_REJECTED: 'delivery_rejected',
  CHECKLIST_ISSUE: 'checklist_issue',
  CALIBRATION_FAILED: 'calibration_failed',
  COOLING_TIME_EXCEEDED: 'cooling_time_exceeded',
  HOLDING_TIME_EXCEEDED: 'holding_time_exceeded',
})

export const FOOD_SAFETY_EXCEPTION_KIND_LABELS = Object.freeze({
  [FOOD_SAFETY_EXCEPTION_KIND.OUT_OF_RANGE]: 'Outside range',
  [FOOD_SAFETY_EXCEPTION_KIND.DELIVERY_REJECTED]: 'Delivery rejected',
  [FOOD_SAFETY_EXCEPTION_KIND.CHECKLIST_ISSUE]: 'Checklist issue',
  [FOOD_SAFETY_EXCEPTION_KIND.CALIBRATION_FAILED]: 'Probe calibration failed',
  [FOOD_SAFETY_EXCEPTION_KIND.COOLING_TIME_EXCEEDED]: 'Cooling took too long',
  [FOOD_SAFETY_EXCEPTION_KIND.HOLDING_TIME_EXCEEDED]: 'Held too long',
})

/** Recording a corrective action resolves the exception; a recheck is optional. */
export const FOOD_SAFETY_EXCEPTION_STATUS = Object.freeze({
  OPEN: 'open',
  RESOLVED: 'resolved',
})

/** "Recheck by" reminder offsets (minutes); shown inside OMS only. */
export const FOOD_SAFETY_RECHECK_REMINDER_MINUTES = Object.freeze([15, 30, 60])

export const FOOD_SAFETY_EXCEPTION_STATUS_LABELS = Object.freeze({
  [FOOD_SAFETY_EXCEPTION_STATUS.OPEN]: 'Needs corrective action',
  [FOOD_SAFETY_EXCEPTION_STATUS.RESOLVED]: 'Corrective action recorded',
})

export const FOOD_SAFETY_CORRECTIVE_ACTION = Object.freeze({
  MOVED_FOOD: 'moved_food',
  ADJUSTED_EQUIPMENT: 'adjusted_equipment',
  CALLED_ENGINEER: 'called_engineer',
  DISCARDED_FOOD: 'discarded_food',
  REJECTED_DELIVERY: 'rejected_delivery',
  REHEATED: 'reheated',
  COOKED_LONGER: 'cooked_longer',
  CLEANED_AGAIN: 'cleaned_again',
  REPLACED_PROBE: 'replaced_probe',
  PROBE_OUT_OF_USE: 'probe_out_of_use',
  OTHER: 'other',
})

export const FOOD_SAFETY_CORRECTIVE_ACTION_LABELS = Object.freeze({
  [FOOD_SAFETY_CORRECTIVE_ACTION.MOVED_FOOD]: 'Moved food',
  [FOOD_SAFETY_CORRECTIVE_ACTION.ADJUSTED_EQUIPMENT]: 'Adjusted equipment',
  [FOOD_SAFETY_CORRECTIVE_ACTION.CALLED_ENGINEER]: 'Called engineer',
  [FOOD_SAFETY_CORRECTIVE_ACTION.DISCARDED_FOOD]: 'Discarded food',
  [FOOD_SAFETY_CORRECTIVE_ACTION.REJECTED_DELIVERY]: 'Rejected delivery',
  [FOOD_SAFETY_CORRECTIVE_ACTION.REHEATED]: 'Reheated',
  [FOOD_SAFETY_CORRECTIVE_ACTION.COOKED_LONGER]: 'Cooked longer',
  [FOOD_SAFETY_CORRECTIVE_ACTION.CLEANED_AGAIN]: 'Cleaned again',
  [FOOD_SAFETY_CORRECTIVE_ACTION.REPLACED_PROBE]: 'Replaced probe',
  [FOOD_SAFETY_CORRECTIVE_ACTION.PROBE_OUT_OF_USE]: 'Took probe out of use',
  [FOOD_SAFETY_CORRECTIVE_ACTION.OTHER]: 'Other',
})

/** Actions that must come with a written note. */
export const FOOD_SAFETY_CORRECTIVE_ACTIONS_REQUIRING_NOTE = Object.freeze([
  FOOD_SAFETY_CORRECTIVE_ACTION.OTHER,
])

/** Cleaning & checklist step outcome. Only `issue` opens an exception. */
export const FOOD_SAFETY_CHECKLIST_STEP_STATUS = Object.freeze({
  DONE: 'done',
  NA: 'na',
  ISSUE: 'issue',
})

export const FOOD_SAFETY_CHECKLIST_STEP_STATUS_LABELS = Object.freeze({
  [FOOD_SAFETY_CHECKLIST_STEP_STATUS.DONE]: 'Done',
  [FOOD_SAFETY_CHECKLIST_STEP_STATUS.NA]: 'N/A',
  [FOOD_SAFETY_CHECKLIST_STEP_STATUS.ISSUE]: 'Issue',
})

export const FOOD_SAFETY_CHECKLIST_NA_REASON_MAX_LENGTH = 500
export const FOOD_SAFETY_CHECKLIST_ISSUE_MAX_LENGTH = 1000

/** Units that can be marked out of service (fridges & freezers). */
export const FOOD_SAFETY_OUT_OF_SERVICE_SECTIONS = Object.freeze([
  FOOD_SAFETY_SECTION.FRIDGES_FREEZERS,
])

export const FOOD_SAFETY_OUT_OF_SERVICE_REASON_MAX_LENGTH = 500

/** How far back a manager can start out of service (same limit as Express). */
export const FOOD_SAFETY_OUT_OF_SERVICE_BACKDATE_MAX_DAYS = 7

/** Venue time when Express hasn't sent one (Express's own default). */
export const FOOD_SAFETY_DEFAULT_TIMEZONE = 'Europe/London'

/** Probe calibration tests and their suggested (editable) pass bands. */
export const FOOD_SAFETY_CALIBRATION_TEST = Object.freeze({
  ICE_WATER: 'ice_water',
  BOILING_WATER: 'boiling_water',
  /** One check records both tests (ice is `value`, boiling is `boilingValue`). */
  ICE_AND_BOILING: 'ice_and_boiling',
})

export const FOOD_SAFETY_CALIBRATION_DEFAULTS = Object.freeze({
  [FOOD_SAFETY_CALIBRATION_TEST.ICE_WATER]: Object.freeze({
    label: 'Ice water',
    target: 0,
    tempMin: -1,
    tempMax: 1,
  }),
  [FOOD_SAFETY_CALIBRATION_TEST.BOILING_WATER]: Object.freeze({
    label: 'Boiling water',
    target: 100,
    tempMin: 99,
    tempMax: 101,
  }),
  [FOOD_SAFETY_CALIBRATION_TEST.ICE_AND_BOILING]: Object.freeze({
    label: 'Ice + boiling water',
    target: 0,
    tempMin: -1,
    tempMax: 1,
    boilingTarget: 100,
  }),
})

export const FOOD_SAFETY_CALIBRATION_DEFAULT_TEST =
  FOOD_SAFETY_CALIBRATION_TEST.ICE_WATER

/** Allowed probe tolerance (± °C). */
export const FOOD_SAFETY_CALIBRATION_TOLERANCE_BOUNDS = Object.freeze({
  min: 0.1,
  max: 5,
})

/** A unit in the probes section. Missing on an older probe means probe. */
export const FOOD_SAFETY_EQUIPMENT_KIND = Object.freeze({
  PROBE: 'probe',
  DISHWASHER: 'dishwasher',
})

/** Paper form K12. The kitchen can change these per machine. */
export const FOOD_SAFETY_DISHWASHER_DEFAULTS = Object.freeze({
  wash: Object.freeze({ label: 'Wash', target: 55 }),
  rinse: Object.freeze({ label: 'Rinse', target: 82 }),
})

export const FOOD_SAFETY_PROBE_REFERENCE_MAX_LENGTH = 120

/** Suggested venue holding range until the venue saves its own. */
export const FOOD_SAFETY_HOLDING_SUGGESTED_RANGES = Object.freeze({
  [FOOD_SAFETY_SECTION.HOT_HOLDING]: Object.freeze({
    tempMin: 63,
    tempMax: null,
  }),
  [FOOD_SAFETY_SECTION.COLD_HOLDING]: Object.freeze({
    tempMin: null,
    tempMax: 8,
  }),
})

/** Optional hot holding max time — opt-in per venue, never defaulted. */
export const FOOD_SAFETY_HOLDING_TIMED_SECTIONS = Object.freeze([
  FOOD_SAFETY_SECTION.HOT_HOLDING,
])

export const FOOD_SAFETY_HOLDING_MAX_MINUTES_BOUNDS = Object.freeze({
  min: 5,
  max: 720,
})

/**
 * Side drawer width that never overflows a phone: the desktop width, capped
 * at the screen width.
 */
export const foodSafetyDrawerWidth = px => `min(${px}px, 100vw)`

/** Suggested venue cooling target until the venue saves its own (Express). */
export const FOOD_SAFETY_COOLING_SUGGESTED_TARGET = Object.freeze({
  tempMax: 8,
  maxMinutes: null,
})

/** Goods Receipt → delivery checks. */
export const FOOD_SAFETY_PURCHASE_ORDER_NUMBER_MAX_LENGTH = 64
export const FOOD_SAFETY_RECENT_RECEIPTS_DAYS = Object.freeze({
  default: 7,
  max: 31,
})
export const FOOD_SAFETY_RECENT_RECEIPTS_LIMIT = Object.freeze({
  default: 20,
  max: 50,
})
export const FOOD_SAFETY_DELIVERY_BATCH_MAX_CHECKS = 50

/** Setup readiness — "have I finished setting up Food Safety?" */
export const FOOD_SAFETY_READINESS_STATE = Object.freeze({
  NOT_CONFIGURED: 'not_configured',
  CONFIGURED: 'configured',
  OPTIONAL: 'optional',
  NEEDS_ATTENTION: 'needs_attention',
})

export const FOOD_SAFETY_READINESS_STATE_LABELS = Object.freeze({
  [FOOD_SAFETY_READINESS_STATE.NOT_CONFIGURED]: 'Not configured',
  [FOOD_SAFETY_READINESS_STATE.CONFIGURED]: 'Configured',
  [FOOD_SAFETY_READINESS_STATE.OPTIONAL]: 'Optional',
  [FOOD_SAFETY_READINESS_STATE.NEEDS_ATTENTION]: 'Needs attention',
})

/** Non-section readiness areas. */
export const FOOD_SAFETY_READINESS_AREA = Object.freeze({
  REPORT_RECIPIENTS: 'report_recipients',
  STAFF_PREVIEW: 'staff_preview',
})

export const FOOD_SAFETY_READINESS_AREA_LABELS = Object.freeze({
  [FOOD_SAFETY_SECTION.FRIDGES_FREEZERS]: 'Fridges & Freezers',
  [FOOD_SAFETY_SECTION.COOKING_REHEATING]: 'Cooking & Reheating',
  [FOOD_SAFETY_SECTION.COOLING]: 'Cooling',
  [FOOD_SAFETY_SECTION.DELIVERIES]: 'Deliveries',
  [FOOD_SAFETY_SECTION.CLEANING_CHECKLISTS]: 'Cleaning & Checklists',
  [FOOD_SAFETY_SECTION.HOT_HOLDING]: 'Hot Holding',
  [FOOD_SAFETY_SECTION.COLD_HOLDING]: 'Cold Holding',
  [FOOD_SAFETY_SECTION.PROBE_CALIBRATION]: 'Temperature Probes',
  [FOOD_SAFETY_SECTION.DISHWASHERS]: 'Dishwashers',
  [FOOD_SAFETY_SECTION.LEGACY_EQUIPMENT]: 'Unclassified historical equipment',
  [FOOD_SAFETY_READINESS_AREA.REPORT_RECIPIENTS]: 'Manager / report recipients',
  [FOOD_SAFETY_READINESS_AREA.STAFF_PREVIEW]: 'Staff day-to-day preview',
})

/** Never "your venue is compliant" — OMS evidences the venue's own system. */
export const FOOD_SAFETY_READINESS_COPY = Object.freeze({
  complete: 'Your OMS Food Safety setup is complete.',
  responsibility:
    'Each venue remains responsible for deciding the food safety controls its operation needs.',
})

/** Why a definition revision was written. */
export const FOOD_SAFETY_DEFINITION_REVISION_ACTION = Object.freeze({
  BASELINE: 'baseline',
  CREATED: 'created',
  UPDATED: 'updated',
  ARCHIVED: 'archived',
  RESTORED: 'restored',
  OUT_OF_SERVICE: 'out_of_service',
  RETURNED_TO_SERVICE: 'returned_to_service',
})

export const FOOD_SAFETY_REPORTS_PAYWALL_REASON = Object.freeze({
  OLDER: 'older',
  DOWNLOAD: 'download',
  EMAIL: 'email',
})

/**
 * Prefill for Ask Ollie Food Safety coach (Step 19.12).
 * Opens Ollie with this draft; dedicated Food Safety training content is a follow-up.
 */
export const FOOD_SAFETY_OLLIE_COACH_DRAFT =
  'Walk me through Food Safety on OMS — setup, what the kitchen logs each day, and how reports work.'

export const FOOD_SAFETY_OLLIE_COACH_LABEL = 'Ask Ollie how Food Safety works'

/** Documents — a simple upload-and-store shelf (mirrors Express). */
export const FOOD_SAFETY_DOCUMENT_CATEGORY = Object.freeze({
  PEST_CONTROL: 'pest_control',
  CERTIFICATES: 'certificates',
  TRAINING: 'training',
  SERVICING: 'servicing',
  OTHER: 'other',
})

export const FOOD_SAFETY_DOCUMENT_CATEGORY_LABELS = Object.freeze({
  [FOOD_SAFETY_DOCUMENT_CATEGORY.PEST_CONTROL]: 'Pest control',
  [FOOD_SAFETY_DOCUMENT_CATEGORY.CERTIFICATES]: 'Certificates',
  [FOOD_SAFETY_DOCUMENT_CATEGORY.TRAINING]: 'Training',
  [FOOD_SAFETY_DOCUMENT_CATEGORY.SERVICING]: 'Servicing & maintenance',
  [FOOD_SAFETY_DOCUMENT_CATEGORY.OTHER]: 'Other',
})

export const FOOD_SAFETY_DOCUMENT_MAX_BYTES = 20 * 1024 * 1024
export const FOOD_SAFETY_DOCUMENT_TITLE_MAX_LENGTH = 120
export const FOOD_SAFETY_DOCUMENT_EXTENSIONS = Object.freeze([
  'pdf',
  'jpg',
  'jpeg',
  'png',
  'heic',
  'heif',
  'webp',
  'doc',
  'docx',
  'xls',
  'xlsx',
])
